import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import { CircleCheck, Loader2, TriangleAlert } from "lucide-react"

import { cn } from "@/lib/utils"
import { formatNumber } from "@/lib/format"

/*
  TabsBar — the page tab row (kit `.tabs`): icon · label · count pill (navy when active) · amber
  attention dot, with an optional status on the right ("All changes saved" / "3 unsaved changes").
  Built on Radix Tabs: ←/→ move between tabs (roving focus), Enter/Space opens one (manual activation,
  so arrowing past a tab that loads data does not load it), Home/End jump. On phones the row scrolls
  sideways and keeps the open tab in view.

    // inside your own <Tabs> root (panels = <TabsContent>):
    <Tabs value={tab} onValueChange={setTab} activationMode="manual">
      <TabsBar tabs={tabs} status={<SaveStatus dirtyCount={dirty} />} />
      <TabsContent value="stages">…</TabsContent>
    </Tabs>
    // or on its own (the page swaps content itself):
    const [tab, setTab] = useTabParam(tabs, { guard: () => confirmLeave() });
    <TabsBar tabs={tabs} value={tab} onValueChange={setTab} ariaLabel="Project sections" />

  tabs: [{ value, label, icon, count (number or text like '2/9'), warn (true or a reason string → amber
  dot + screen-reader text), badge (node after the count, e.g. "6 low"), disabled, hidden, title, testId
  (default `tab-<value>`), controls (standalone only: id of the page's role="tabpanel" element) }].
  Trigger ids stay Radix's `…-trigger-<value>` (tests and shot.js use them).
  Props: tabs, value + onValueChange (standalone mode), activationMode ('manual'), status (right slot,
  hidden on phones), variant ('underline' | 'pills' — the phone look of 04 mobile), ariaLabel,
  className, listClassName, testId.
*/

function TabCount({ count }) {
  if (count === null || count === undefined || count === "") return null
  const text = typeof count === "number" ? formatNumber(count, { maxDecimals: 0 }) : count
  return (
    <span className="rounded-full bg-[color:var(--status-gray-bg)] px-[7px] py-px text-[11px] font-bold tabular-nums text-ink-2 group-data-[state=active]/tab:bg-brand group-data-[state=active]/tab:text-brand-fg">
      {text}
    </span>
  )
}

function TabWarn({ warn }) {
  if (!warn) return null
  const reason = typeof warn === "string" ? warn : "Needs attention"
  return (
    <>
      <span className="ui-tone-dot tone-amber h-[7px] w-[7px]" title={reason} aria-hidden="true" />
      <span className="sr-only">({reason})</span>
    </>
  )
}

const UNDERLINE_TRIGGER =
  "group/tab relative -mx-1 inline-flex shrink-0 items-center gap-[7px] whitespace-nowrap rounded-md px-1 pb-[11px] pt-2.5 text-[13.5px] font-semibold text-ink-3 outline-none transition-colors hover:text-ink-2 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 data-[state=active]:text-ink after:absolute after:inset-x-1 after:bottom-0 after:h-[2.5px] after:rounded-t-sm after:bg-transparent data-[state=active]:after:bg-brand [&>svg]:h-[15px] [&>svg]:w-[15px] [&>svg]:shrink-0"

const PILL_TRIGGER =
  "group/tab inline-flex h-[34px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-[13.5px] font-semibold text-ink-2 outline-none transition-colors hover:text-ink focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-surface data-[state=active]:text-ink data-[state=active]:shadow-card [&>svg]:h-[15px] [&>svg]:w-[15px] [&>svg]:shrink-0"

function TabsBarList({ tabs, status, variant, ariaLabel, className, listClassName, testId, activeValue, standalone }) {
  const listRef = React.useRef(null)
  const [edges, setEdges] = React.useState({ left: false, right: false })
  const pills = variant === "pills"

  const measure = React.useCallback(() => {
    const list = listRef.current
    if (!list) return
    const left = list.scrollLeft > 2
    const right = list.scrollLeft + list.clientWidth < list.scrollWidth - 2
    setEdges((current) => (current.left === left && current.right === right ? current : { left, right }))
  }, [])

  // Keep the open tab in view when the row scrolls sideways (phones) — without scrolling the page
  // (scrollIntoView could). Watches Radix's data-state, so it also works inside a page's own <Tabs>.
  const revealActive = React.useCallback(() => {
    const list = listRef.current
    if (!list) return
    const active = list.querySelector('[role="tab"][data-state="active"]')
    if (active) {
      const start = active.offsetLeft - 16
      const end = active.offsetLeft + active.offsetWidth + 16
      if (start < list.scrollLeft) list.scrollLeft = Math.max(0, start)
      else if (end > list.scrollLeft + list.clientWidth) list.scrollLeft = end - list.clientWidth
    }
    measure()
  }, [measure])

  React.useLayoutEffect(() => {
    revealActive()
  }, [activeValue, tabs.length, revealActive])

  React.useEffect(() => {
    const list = listRef.current
    if (!list || typeof MutationObserver === "undefined") return undefined
    const observer = new MutationObserver(revealActive)
    observer.observe(list, { subtree: true, attributes: true, attributeFilter: ["data-state"] })
    return () => observer.disconnect()
  }, [revealActive])

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined
    window.addEventListener("resize", measure)
    return () => window.removeEventListener("resize", measure)
  }, [measure])

  const fade = edges.left && edges.right
    ? "[mask-image:linear-gradient(to_right,transparent,black_24px,black_calc(100%-24px),transparent)]"
    : edges.right
      ? "[mask-image:linear-gradient(to_right,black_calc(100%-24px),transparent)]"
      : edges.left
        ? "[mask-image:linear-gradient(to_right,transparent,black_24px)]"
        : ""

  return (
    <div className={cn("flex min-w-0 items-center gap-4", !pills && "border-b border-line", className)} data-testid={testId}>
      <TabsPrimitive.List
        ref={listRef}
        aria-label={ariaLabel}
        onScroll={measure}
        className={cn(
          // relative: revealActive measures tabs by offsetLeft, which must be relative to this scrolling list
          "relative flex min-w-0 flex-1 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          pills ? "gap-0.5 rounded-[11px] bg-[color:var(--status-gray-bg)] p-[3px]" : "gap-[22px] px-1",
          fade,
          listClassName
        )}
      >
        {tabs.filter((tab) => tab && !tab.hidden).map((tab) => {
          const Icon = tab.icon
          return (
            <TabsPrimitive.Trigger
              key={tab.value}
              value={tab.value}
              disabled={tab.disabled}
              title={tab.title}
              data-testid={tab.testId || `tab-${tab.value}`}
              className={pills ? PILL_TRIGGER : UNDERLINE_TRIGGER}
              // Standalone, there is no <TabsContent>: Radix's aria-controls would point at a panel id
              // that does not exist. `tab.controls` = the id of the page's own role="tabpanel", if any.
              {...(standalone ? { "aria-controls": tab.controls } : null)}
            >
              {Icon ? <Icon aria-hidden="true" /> : null}
              <span>{tab.label}</span>
              <TabCount count={tab.count} />
              {tab.badge}
              <TabWarn warn={tab.warn} />
            </TabsPrimitive.Trigger>
          )
        })}
      </TabsPrimitive.List>
      {status ? <div className={cn("hidden shrink-0 items-center sm:flex", !pills && "pb-1")}>{status}</div> : null}
    </div>
  )
}

function TabsBar({
  tabs = [],
  value,
  onValueChange,
  activationMode = "manual",
  status,
  variant = "underline",
  ariaLabel,
  className,
  listClassName,
  testId = "tabs-bar",
}) {
  const list = (
    <TabsBarList
      tabs={tabs}
      status={status}
      variant={variant}
      ariaLabel={ariaLabel}
      className={className}
      listClassName={listClassName}
      testId={testId}
      activeValue={value}
      standalone={value !== undefined}
    />
  )
  if (value === undefined) return list
  return (
    <TabsPrimitive.Root value={value} onValueChange={onValueChange} activationMode={activationMode}>
      {list}
    </TabsPrimitive.Root>
  )
}

/*
  SaveStatus — the quiet save state for the right of a TabsBar or a toolbar:
  "✓ All changes saved" · "● 3 unsaved changes" · "Saving…" · "⚠ Couldn’t save".
  Props: dirtyCount, saving, error (text → shown as the tooltip), savedLabel, className.
*/
function SaveStatus({ dirtyCount = 0, saving = false, error, savedLabel = "All changes saved", className }) {
  let content
  if (saving) {
    content = (<><Loader2 className="h-3.5 w-3.5 animate-spin text-ink-3" aria-hidden="true" />Saving…</>)
  } else if (error) {
    content = (<><TriangleAlert className="h-3.5 w-3.5 text-status-red" aria-hidden="true" /><span className="text-status-red-ink">Couldn’t save</span></>)
  } else if (dirtyCount > 0) {
    content = (
      <>
        <span className="ui-tone-dot tone-amber h-2 w-2" aria-hidden="true" />
        <span className="font-semibold text-status-amber-ink">
          {formatNumber(dirtyCount, { maxDecimals: 0 })} unsaved {dirtyCount === 1 ? "change" : "changes"}
        </span>
      </>
    )
  } else {
    content = (<><CircleCheck className="h-3.5 w-3.5 text-status-green" aria-hidden="true" />{savedLabel}</>)
  }
  return (
    <span
      role="status"
      title={error ? String(error) : undefined}
      className={cn("inline-flex items-center gap-1.5 whitespace-nowrap text-[12.5px] text-ink-3", className)}
      data-testid="save-status"
    >
      {content}
    </span>
  )
}

export { TabsBar, SaveStatus }
